import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle, Users, GraduationCap, BookOpen, Award, Clock, Briefcase, Target, Search, ArrowRight } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { SEO } from '../components/SEO';
import { getStandardBySlug } from '../data/standards/standardsData';
import { getOrderedTrainingTypes } from '../data/standards/trainingTypes';

// Icon mapping for whoShouldAttend
const iconMap = {
  Users,
  Briefcase,
  Target,
  Search
};

export const StandardDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();

  // Get standard data from static source
  const standard = getStandardBySlug(slug);
  const trainingTypes = getOrderedTrainingTypes();

  if (!standard) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-800 mb-4">Standard not found</h1>
          <Button onClick={() => navigate('/training/iso-training-services')}>
            Back to ISO Training Services
          </Button>
        </div>
      </div>
    );
  }

  // Course structured data for SEO
  const courseStructuredData = {
    "@context": "https://schema.org",
    "@type": "Course",
    "name": `${standard.code} - ${standard.name}`,
    "description": standard.detailedDescription,
    "provider": {
      "@type": "EducationalOrganization",
      "name": "ISDE Academy",
      "sameAs": "https://isde.academy",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Chennai",
        "addressRegion": "Tamil Nadu",
        "addressCountry": "India"
      }
    },
    "hasCourseInstance": trainingTypes.map(training => ({
      "@type": "CourseInstance",
      "courseMode": "onsite",
      "courseWorkload": training.duration === '1 Day' ? 'PT8H' : training.duration === '3 Days' ? 'PT24H' : 'PT40H',
      "name": training.title,
      "educationalLevel": training.level,
      "description": training.description
    })),
    "educationalCredentialAwarded": "Certificate of Completion",
    "teaches": standard.keyTopics.awareness.concat(
      standard.keyTopics.internalAuditor,
      standard.keyTopics.leadAuditor
    )
  };

  // Breadcrumb structured data
  const breadcrumbStructuredData = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://isde.academy"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "ISO Training Services",
        "item": "https://isde.academy/training/iso-training-services"
      },
      {
        "@type": "ListItem",
        "position": 3,
        "name": standard.code,
        "item": `https://isde.academy/training/${slug}`
      }
    ]
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO
        title={standard.seo.metaTitle}
        description={standard.seo.metaDescription}
        keywords={standard.seo.keywords}
        canonical={`/training/${slug}`}
        ogImage={standard.seo.ogImage}
        structuredData={[courseStructuredData, breadcrumbStructuredData]}
      />

      {/* Hero Section */}
      <div className={`bg-gradient-to-r ${standard.color.gradient} text-white pt-24 pb-16 mt-16`}>
        <div className="max-w-7xl mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            {/* Back Button */}
            <button
              onClick={() => navigate('/training/iso-training-services')}
              className="flex items-center gap-2 text-white/90 hover:text-white mb-6 transition-colors group"
            >
              <ArrowRight className="h-4 w-4 rotate-180 group-hover:-translate-x-1 transition-transform" />
              <span className="text-sm font-medium">Back to ISO Training Services</span>
            </button>

            <div className="inline-block bg-white/20 backdrop-blur-sm px-3 py-1 rounded-full text-sm mb-4 font-semibold">
              {standard.code}
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-4">{standard.name}</h1>
            <p className="text-xl text-blue-100 max-w-3xl">{standard.shortDescription}</p>
          </motion.div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-12">
        {/* Overview Section */}
        <div className="bg-white rounded-lg shadow-md p-8 mb-8">
          <h2 className="text-2xl font-bold mb-4 text-gray-800">Overview</h2>
          <p className="text-gray-700 leading-relaxed mb-4">{standard.description}</p>
          <p className="text-gray-700 leading-relaxed">{standard.detailedDescription}</p>
        </div>

        {/* Benefits and Audience Grid */}
        <div className="grid md:grid-cols-2 gap-8 mb-12">
          <Card>
            <CardContent className="p-6">
              <h2 className="text-2xl font-bold mb-4 flex items-center gap-2 text-gray-800">
                <CheckCircle className="h-6 w-6 text-green-600" />
                Key Benefits
              </h2>
              <ul className="space-y-3">
                {standard.benefits.map((benefit, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span className="text-green-600 mt-1 text-xl flex-shrink-0">✓</span>
                    <span className="text-gray-700">{benefit}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <h2 className="text-2xl font-bold mb-4 flex items-center gap-2 text-gray-800">
                <Users className="h-6 w-6 text-blue-600" />
                Who Should Attend
              </h2>
              <div className="space-y-4">
                {standard.whoShouldAttend.map((audience, i) => {
                  const IconComponent = iconMap[audience.icon] || Users;
                  return (
                    <div key={i} className="flex items-start gap-3">
                      <div className="bg-blue-50 p-2 rounded-lg flex-shrink-0">
                        <IconComponent className="h-5 w-5 text-blue-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900">{audience.title}</h3>
                        <p className="text-sm text-gray-600">{audience.description}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Available Training Programs Section */}
        <div className="mb-12">
          <h2 className="text-3xl font-bold mb-2 text-center text-gray-800">Available Training Programs</h2>
          <p className="text-center text-gray-600 mb-8">Choose the training level that matches your needs</p>

          <div className="grid md:grid-cols-3 gap-6">
            {trainingTypes.map((training, index) => {
              const IconComponent = training.icon;
              const topicKey = training.id === 'internal-auditor' ? 'internalAuditor' : training.id === 'lead-auditor' ? 'leadAuditor' : 'awareness';

              return (
                <motion.div
                  key={training.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Card className="h-full hover:shadow-xl transition-all duration-300 border-2 border-transparent hover:border-blue-200 cursor-pointer group">
                    <CardContent className="p-6 flex flex-col h-full">
                      <div className={`${training.bgColor} w-16 h-16 rounded-xl flex items-center justify-center mb-4`}>
                        <IconComponent className={`h-8 w-8 ${training.color}`} />
                      </div>

                      <h3 className="text-xl font-bold mb-2 text-gray-900 group-hover:text-blue-600 transition-colors">
                        {training.title}
                      </h3>
                      <p className={`text-sm font-semibold ${training.color} mb-3 flex items-center gap-2`}>
                        <Clock className="h-4 w-4" />
                        {training.duration}
                      </p>

                      <p className="text-gray-600 mb-4 text-sm">{training.detailedDescription}</p>

                      {/* Key Topics for this level */}
                      <div className="mb-4 flex-1">
                        <h4 className="font-semibold text-gray-800 mb-2 text-sm">Key Topics:</h4>
                        <ul className="space-y-1">
                          {standard.keyTopics[topicKey].slice(0, 4).map((topic, i) => (
                            <li key={i} className="flex items-start gap-2 text-xs text-gray-600">
                              <span className={`${training.color} mt-0.5`}>•</span>
                              <span className="line-clamp-1">{topic}</span>
                            </li>
                          ))}
                          {standard.keyTopics[topicKey].length > 4 && (
                            <li className="text-xs text-gray-500 italic">
                              +{standard.keyTopics[topicKey].length - 4} more topics
                            </li>
                          )}
                        </ul>
                      </div>

                      {/* Target Audience */}
                      <div className="mb-4">
                        <h4 className="font-semibold text-gray-800 mb-2 text-sm">Who Should Attend:</h4>
                        <ul className="space-y-1">
                          {training.targetAudience.slice(0, 2).map((audience, i) => (
                            <li key={i} className="text-xs text-gray-600 flex items-start gap-1">
                              <span className="text-blue-600">✓</span>
                              <span>{audience}</span>
                            </li>
                          ))}
                        </ul>
                      </div>

                      <div className="mt-auto space-y-3">
                        <div className="text-sm text-gray-500 pb-3 border-b border-gray-200">
                          <span className="font-semibold">Price:</span> {training.price}
                        </div>

                        <div className="grid grid-cols-2 gap-2">
                          <Button
                            onClick={(e) => {
                              e.stopPropagation();
                              const categoryUrl = training.id === 'awareness' ? 'awareness'
                                : training.id === 'internal-auditor' ? 'internal-auditor'
                                : 'lead-auditor';
                              navigate(`/training/${slug}/${categoryUrl}`);
                            }}
                            variant="outline"
                            className="text-xs"
                            size="sm"
                          >
                            Learn More
                          </Button>
                          <Button
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate('/contact');
                            }}
                            className={`text-xs ${training.color.replace('text-', 'bg-')} hover:opacity-90 text-white`}
                            size="sm"
                          >
                            Enroll Now
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        </div>

        {/* Key Topics Covered Section - Detailed View */}
        <div className="bg-white rounded-lg shadow-md p-8 mb-8">
          <h2 className="text-3xl font-bold mb-2 text-center text-gray-800">Complete Curriculum</h2>
          <p className="text-center text-gray-600 mb-8">Comprehensive topics covered at each training level</p>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Awareness Level */}
            <div>
              <div className="flex items-center gap-2 mb-4">
                <div className="bg-emerald-50 p-2 rounded-lg">
                  <GraduationCap className="h-6 w-6 text-emerald-600" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-emerald-600">Awareness Level</h3>
                  <p className="text-xs text-gray-500">1 Day Program</p>
                </div>
              </div>
              <ul className="space-y-2">
                {standard.keyTopics.awareness.map((topic, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm">
                    <span className="text-emerald-600 mt-0.5 flex-shrink-0">•</span>
                    <span className="text-gray-700">{topic}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Internal Auditor Level */}
            <div>
              <div className="flex items-center gap-2 mb-4">
                <div className="bg-blue-50 p-2 rounded-lg">
                  <BookOpen className="h-6 w-6 text-blue-600" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-blue-600">Internal Auditor</h3>
                  <p className="text-xs text-gray-500">3 Days Program</p>
                </div>
              </div>
              <ul className="space-y-2">
                {standard.keyTopics.internalAuditor.map((topic, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm">
                    <span className="text-blue-600 mt-0.5 flex-shrink-0">•</span>
                    <span className="text-gray-700">{topic}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Lead Auditor Level */}
            <div>
              <div className="flex items-center gap-2 mb-4">
                <div className="bg-purple-50 p-2 rounded-lg">
                  <Award className="h-6 w-6 text-purple-600" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-purple-600">Lead Auditor</h3>
                  <p className="text-xs text-gray-500">5 Days Program</p>
                </div>
              </div>
              <ul className="space-y-2">
                {standard.keyTopics.leadAuditor.map((topic, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm">
                    <span className="text-purple-600 mt-0.5 flex-shrink-0">•</span>
                    <span className="text-gray-700">{topic}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className={`bg-gradient-to-r ${standard.color.gradient} text-white rounded-lg p-8 text-center`}>
          <h3 className="text-2xl md:text-3xl font-bold mb-4">Ready to Get Certified?</h3>
          <p className="mb-6 text-blue-100 text-lg">Join thousands of professionals who have enhanced their careers with {standard.code} training</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              onClick={() => navigate('/training-calendar')}
              className="bg-white text-blue-600 hover:bg-gray-100 font-semibold px-8 py-6 text-lg"
              size="lg"
            >
              View Training Calendar
            </Button>
            <Button
              onClick={() => navigate('/contact')}
              variant="outline"
              className="border-2 border-white text-white hover:bg-white hover:text-blue-600 font-semibold px-8 py-6 text-lg"
              size="lg"
            >
              Request Information
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
